import { useContext, useEffect, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  List,
  ListItem,
  Paper,
  Toolbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { format } from "date-fns";

import { AppContext } from "../../context/AppProvider";
import { AuthContext } from "../../context/AuthProvider";
import CustomDataGrid from "../../components/CustomDataGrid";
import ConfirmFulfill from "../../components/ConfirmFulfill";
import { ROW_HEIGHT } from "../../constants/Constants";
import SuccessDialog from "./SuccessDialog";
import { fetchWithInterceptor } from "../../utils/fetchWithInterceptor";

export default function DetailViewDialog({ open, onClose, itemId }) {
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const [confirmVisible, setConfirmVisible] = useState(false);
  const [successDialogVisible, setSuccessDialogVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [orders, setOrders] = useState([]);
  const [manualOrders, setManualOrders] = useState([]);

  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const [selectionModel, setSelectionModel] = useState({
    type: "include",
    ids: new Set(),
  });

  const handleClose = () => {
    setConfirmVisible(false);
    setOrders([]);
    setManualOrders([]);
    setIsLoading(false);
    setSuccessDialogVisible(false);
    setSelectionEnabled(false);
    setSelectionModel({ type: "include", ids: new Set() });
    onClose();
  };

  const onSubmit = () => setConfirmVisible(true);
  const handleCancel = () => setConfirmVisible(false);
  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithInterceptor("/api/ats-fulfillment/mark-as-fulfilled", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
        body: JSON.stringify({
          orderIds: Array.from(selectionModel.ids),
          fileId: itemId,
        }),
      });
      setSelectionModel({ type: "include", ids: new Set() });
      setConfirmVisible(false);
      if (response.ok) {
        setSuccessDialogVisible(true);
      } else {
        const error = await response.json();
        console.error("Failed to mark orders as Fulfilled", error);
        appContext.showMessage(error?.error || "Failed to mark orders as Fulfilled", "error");
      }
    } catch (error) {
      console.error("Error marking orders as Fulfilled:", error);
      appContext.showMessage("Error marking orders as Fulfilled", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchOrders = async () => {
      if (!itemId) {
        return;
      }
      setIsLoading(true);
      try {
        const response = await fetchWithInterceptor(
          `/api/ats-fulfillment/orders?fileId=${itemId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-access-token": authContext.getToken(),
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          const ordersAux = data.items.filter((o) => o.orderNumber);
          setOrders(ordersAux);
          setManualOrders(data.items.filter((o) => !o.orderNumber));

          const enabled = data.status === "pending" || data.status === "failed";
          setSelectionEnabled(enabled);
          if (enabled) {
            setSelectionModel({
              type: "include",
              ids: new Set(
                ordersAux.filter((o) => !o.fulfilled).map((o) => o.id)
              ),
            });
          }
        } else {
          console.error("Failed to fetch orders");
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (open) {
      fetchOrders();
    } else {
      setOrders([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, itemId]);

  const columns = [
    {
      field: "orderNumber",
      headerName: "Order Number",
      width: 120,
      sortable: false,
    },
    {
      field: "trackingNumber",
      headerName: "Tracking Number",
      width: 140,
      sortable: false,
    },
    {
      field: "fulfilled",
      headerName: "Fulfilled",
      width: 100,
      sortable: false,
      valueGetter: (value) => (value ? "Yes" : "No"),
    },
    {
      field: "fulfillmentMessage",
      headerName: "Info",
      width: 350,
      sortable: false,
    },
    {
      field: "delivered",
      headerName: "Delivered",
      width: 100,
      sortable: false,
      valueGetter: (value) => (value ? "Yes" : "No"),
    },
    {
      field: "deliveredDate",
      headerName: "Delivered At",
      width: 120,
      sortable: false,
      valueGetter: (value) =>
        value ? format(new Date(value), "yyyy-MM-dd") : "",
    },
    {
      field: "customer",
      headerName: "Customer",
      width: 200,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            sx={{ height: "100%" }}
          >
            <Typography variant="body1">{`${params?.row?.customer}`}</Typography>
            <Typography
              variant="caption"
              color="textSecondary"
            >{`${params?.row?.email}`}</Typography>
          </Box>
        );
      },
    },
    {
      field: "postalCode",
      headerName: "Postal Code",
      width: 120,
      sortable: false,
    },
    { field: "city", headerName: "City", width: 150, sortable: false },
    { field: "province", headerName: "Province", width: 120, sortable: false },
  ];

  return (
    <>
      <Dialog fullScreen open={open} onClose={handleClose}>
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" sx={{ ml: 2, flex: 1 }}>
              ATS Manifest Orders
            </Typography>
          </Toolbar>
        </AppBar>
        <DialogContent>
          {orders.length > 0 && (
            <Box>
              <Box>
                <Typography variant="h6">
                  Select the orders you want to mark as Fulfilled
                </Typography>
              </Box>
              <Box display="flex" justifyContent="flex-end" sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onSubmit}
                  disabled={!selectionModel.ids.size}
                >
                  Mark as Fulfilled & Add Tracking Number
                </Button>
              </Box>

              <CustomDataGrid
                rows={orders}
                columns={columns}
                pageSize={orders.length}
                pageSizeOptions={[orders.length]}
                rowCount={orders.length}
                loading={isLoading}
                checkboxSelection={selectionEnabled}
                rowSelectionModel={selectionModel}
                onRowSelectionModelChange={(model) => setSelectionModel(model)}
                isRowSelectable={(params) => !params?.row?.fulfilled}
                sx={{ height: ROW_HEIGHT * orders.length + 120 }}
              />
            </Box>
          )}

          {manualOrders.length > 0 && (
            <Box>
              <Box>
                <Typography variant="h6">
                  These orders must be handled manually
                </Typography>
              </Box>
              <Paper
                sx={{
                  width: "100%",
                  backgroundColor: "background.paper",
                  mt: 2,
                  mb: 2,
                }}
              >
                <List>
                  {manualOrders.map((value, key) => {
                    return (
                      <ListItem
                        key={key.toString()}
                        role={undefined}
                        sx={{ pt: 2, pb: 2 }}
                      >
                        <Grid container spacing={2} sx={{ width: "100%" }}>
                          <Grid size={{ xs: 5 }}>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                            >{`Tracking number ${value.trackingNumber || "Unknown"}`}</Typography>
                          </Grid>
                          <Grid size={{ xs: 7 }}>
                            <Typography variant="body1">{`${value.customer || "Unknown"}`}</Typography>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                            >{`${value.address || ""}, ${value.postalCode || ""}, ${value.city || ""}, ${value.province || ""}`}</Typography>
                          </Grid>
                        </Grid>
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            </Box>
          )}

          <ConfirmFulfill
            open={confirmVisible}
            count={selectionModel.ids.size}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
          />
        </DialogContent>
      </Dialog>
      <SuccessDialog open={successDialogVisible} onClose={handleClose} />
    </>
  );
}
